/**
 * Photo overlay renderer.
 *
 * One Fabric.js based builder for overlay layers, shared by the overlay editor
 * (interactive preview) and the three bake sites (TakePhotos, tracker
 * PhotoStep, simple tracker). Because the preview and the bake build the same
 * Fabric objects from the same stored layers, what the editor shows is what
 * the photo gets.
 *
 * Fabric is loaded lazily so the main bundle does not carry it.
 */
import WebFont from 'webfontloader'
import { designSizeFor, fitTransform, toDesignSpace, coverCropRect } from '@/util/overlayGeometry'

let fabricPromise = null

/** Horizontal anchors a text layer can store in text_align. */
export const TEXT_ALIGNS = ['left', 'center', 'right']

/** Selection handle styling shared by the overlay editor and the per photo adjust drawer. */
export const LAYER_HANDLE_STYLE = {
  borderColor: '#e14eca',
  cornerColor: '#e14eca',
  cornerStrokeColor: '#ffffff',
  cornerStyle: 'circle',
  cornerSize: 10,
  transparentCorners: false,
}

/**
 * Lazy load Fabric once per page lifetime.
 * @returns {Promise<typeof import('fabric')>}
 */
export function loadFabric() {
  if (!fabricPromise) fabricPromise = import('fabric')
  return fabricPromise
}

/**
 * Load Google fonts by family name. Resolves even when some fail (they are
 * logged); an empty list resolves immediately.
 * @param {string[]} families
 * @returns {Promise<void>}
 */
export function loadFonts(families) {
  const list = [...new Set((families || []).filter(Boolean))]
  if (list.length === 0) return Promise.resolve()
  return new Promise(resolve => {
    try {
      WebFont.load({
        google: { families: list },
        active: () => resolve(),
        inactive: () => {
          console.error('Overlay fonts failed to load', list)
          resolve()
        },
        timeout: 5000,
      })
    } catch (e) {
      console.error('Overlay font loader failed', e)
      resolve()
    }
  })
}

/**
 * Create a Fabric object for one stored layer (design-space coordinates).
 * `text` overrides layer.content for text layers (resolved dynamic tokens).
 * Images use a top-left origin so left/top equal the stored x/y. Text uses
 * its text_align (left, center, right) as the horizontal origin, so x is the
 * anchor point and a dynamic variable that resolves shorter or longer than the
 * placeholder stays centered or right aligned on that point.
 * @param {typeof import('fabric')} fabric
 * @param {Object} layer
 * @param {{text?: string}} [opts]
 * @returns {Promise<import('fabric').FabricObject>}
 */
export async function createLayerObject(fabric, layer, { text } = {}) {
  const x = Number(layer.x) || 0
  const y = Number(layer.y) || 0
  if (layer.type === 'image') {
    const img = await fabric.FabricImage.fromURL(layer.content, { crossOrigin: 'anonymous' })
    const width = Number(layer.width) || img.width
    const height = Number(layer.height) || img.height
    img.set({
      left: x,
      top: y,
      originX: 'left',
      originY: 'top',
      scaleX: width / img.width,
      scaleY: height / img.height,
    })
    img.layerType = 'image'
    return img
  }
  const fontSize = Number(layer.fontSize ?? layer.font_size) || 20
  const align = TEXT_ALIGNS.includes(layer.text_align) ? layer.text_align : 'left'
  const t = new fabric.IText(text != null ? String(text) : String(layer.content || ''), {
    left: x,
    top: y,
    originX: align,
    originY: 'top',
    textAlign: align,
    fontSize,
    fontFamily: layer.font || 'Poppins',
    fill: layer.color || '#000000',
    lockUniScaling: true,
  })
  t.layerType = 'text'
  return t
}

/**
 * Render an overlay's visible layers onto an offscreen canvas. `scale` is the
 * factor applied to design units (1 = design pixels). By default the canvas is
 * the design artboard; pass `frame` (a larger area in design units) and
 * `offset` (where the artboard sits inside it) to render over a whole photo,
 * so adjusted layers dragged outside the artboard still land on the picture.
 * Nothing is retained after the call.
 * @returns {Promise<{canvas: HTMLCanvasElement, design: {width: number, height: number}}>}
 */
export async function renderOverlayStage({ layers, aspectRatio, referenceWidth, referenceHeight, resolveText, scale = 1, frame = null, offset = null }) {
  const fabric = await loadFabric()
  const design = designSizeFor(aspectRatio)
  const area = frame || design
  const shift = offset || { x: 0, y: 0 }
  const ordered = [...(layers || [])].sort((a, b) => Number(a.position) - Number(b.position))
  const converted = toDesignSpace(ordered, referenceWidth, referenceHeight, design)
  await loadFonts(converted.filter(l => l.type === 'text').map(l => l.font))

  const el = document.createElement('canvas')
  const stage = new fabric.StaticCanvas(el, {
    width: area.width,
    height: area.height,
    renderOnAddRemove: false,
  })
  try {
    for (const layer of converted) {
      if (layer.hidden === true) continue
      try {
        const text = layer.type === 'text' && resolveText ? await resolveText(layer.content || '') : undefined
        const obj = await createLayerObject(fabric, layer, { text })
        if (shift.x || shift.y) obj.set({ left: obj.left + shift.x, top: obj.top + shift.y })
        stage.add(obj)
      } catch (e) {
        console.error('Overlay layer failed to render', e)
      }
    }
    return { canvas: stage.toCanvasElement(scale), design }
  } finally {
    stage.dispose()
  }
}

/**
 * Canvas holding the photo a bake draws on. With `crop` the photo is cut to
 * the overlay's aspect ratio (largest centered area); otherwise it is the
 * whole photo. White underneath, like every bake site did before.
 * @param {HTMLImageElement} image loaded original photo
 * @param {{aspectRatio: string, crop: boolean}} opts
 * @returns {{canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, width: number, height: number}}
 */
export function preparePhotoCanvas(image, { aspectRatio, crop }) {
  const pw = image.naturalWidth
  const ph = image.naturalHeight
  const rect = crop ? coverCropRect(designSizeFor(aspectRatio), pw, ph) : { sx: 0, sy: 0, sw: pw, sh: ph }
  const canvas = document.createElement('canvas')
  canvas.width = rect.sw
  canvas.height = rect.sh
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, rect.sw, rect.sh)
  ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh)
  return { canvas, ctx, width: rect.sw, height: rect.sh }
}

/**
 * Bake an overlay onto a 2D context that already holds the photo.
 * The overlay's design canvas is scaled by one uniform factor to fit the
 * photo and centered, so nothing is stretched on an aspect mismatch.
 * Layers with `hidden: true` (a per photo adjustment) are skipped.
 *
 * @param {Object} args
 * @param {CanvasRenderingContext2D} args.ctx target context (photo already drawn)
 * @param {Array<Object>} args.layers parsed layer rows (font_size or fontSize keys)
 * @param {string} args.aspectRatio overlay aspect label, e.g. '16:9'
 * @param {number} args.referenceWidth stored photo_overlays.reference_width
 * @param {number} args.referenceHeight stored photo_overlays.reference_height
 * @param {number} args.photoWidth
 * @param {number} args.photoHeight
 * @param {(content: string) => Promise<string>|string} [args.resolveText] dynamic token resolver
 * @returns {Promise<void>}
 */
export async function bakeOverlay({ ctx, layers, aspectRatio, referenceWidth, referenceHeight, photoWidth, photoHeight, resolveText }) {
  const design = designSizeFor(aspectRatio)
  const { scale, offsetX, offsetY } = fitTransform(design, photoWidth, photoHeight)
  // Render over the whole photo (in design units) so an adjusted layer outside
  // the artboard is kept; template layers sit inside it and land exactly as before.
  const frame = { width: photoWidth / scale, height: photoHeight / scale }
  const offset = { x: offsetX / scale, y: offsetY / scale }
  const { canvas } = await renderOverlayStage({ layers, aspectRatio, referenceWidth, referenceHeight, resolveText, scale, frame, offset })
  ctx.drawImage(canvas, 0, 0)
}
